// Home Page - Landing with goal input and session history
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowRight, Cpu, Wrench, Zap, LogOut,
  Clock, ChevronRight, ChevronLeft, Target, CheckCircle, Settings, Shuffle,
  Pause, Play, MessageSquare, Package, Rocket,
  // Category icons
  Cog, DollarSign, UtensilsCrossed, Leaf, ShoppingCart, Heart, GraduationCap,
  Palette, Home as HomeIcon, Scale, Truck, Sun, Factory, Shield, Film,
  Trophy, Plane, PawPrint, HandHeart, Lightbulb
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import UsageBanner from '../components/UsageBanner';
import useConfigStore from '../store/configStore';
import { useAuth } from '../contexts/AuthContext';
import { getMySessions, getExamples, createSession } from '../services/api';
import {
  trackHomePageView,
  trackGoalSubmit,
  trackExampleSelect,
  trackSessionContinue,
  trackLogout
} from '../services/analytics';

// Category icon mapping
const CATEGORY_ICONS = {
  'hardware': Cog,
  'finance': DollarSign,
  'food': UtensilsCrossed,
  'agriculture': Leaf,
  'ecommerce': ShoppingCart,
  'healthcare': Heart,
  'education': GraduationCap,
  'creative': Palette,
  'realestate': HomeIcon,
  'legal': Scale,
  'transportation': Truck,
  'energy': Sun,
  'manufacturing': Factory,
  'security': Shield,
  'entertainment': Film,
  'sports': Trophy,
  'travel': Plane,
  'home': HomeIcon,
  'pets': PawPrint,
  'nonprofit': HandHeart,
  'default': Lightbulb
};

// Default examples with categories (fallback if Firebase fails)
const DEFAULT_EXAMPLES = [
  { text: "I want to design a new type of electric bicycle", category: "hardware" },
  { text: "Help me build a personal finance dashboard", category: "finance" },
  { text: "I need to automate my restaurant's inventory", category: "food" },
  { text: "Create a tool to analyze my farm's soil data", category: "agriculture" },
  { text: "Build an AI-powered Shopify store assistant", category: "ecommerce" },
  { text: "Create a symptom checker for my clinic", category: "healthcare" },
  { text: "Build an AI tutor for learning mathematics", category: "education" },
  { text: "Create an AI assistant for generating design mockups", category: "creative" },
];

export default function Home() {
  const [inputGoal, setInputGoal] = useState('');
  const [mySessions, setMySessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [examples, setExamples] = useState([]);
  const [loadingExamples, setLoadingExamples] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { setGoal, setSessionId, setCurrentStep, setStack, reset } = useConfigStore();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Load examples from backend
  useEffect(() => {
    const loadExamples = async () => {
      try {
        const data = await getExamples();
        if (data.examples && data.examples.length > 0) {
          // Map backend format to component format (goal -> text)
          const mappedExamples = data.examples.map(ex => ({
            text: ex.goal || ex.text,
            category: ex.category || 'default',
            title: ex.title,
            description: ex.description
          }));
          setExamples(mappedExamples);
        } else {
          // Use default examples if none in backend
          setExamples(DEFAULT_EXAMPLES);
        }
      } catch (error) {
        console.error('Failed to load examples:', error);
        setExamples(DEFAULT_EXAMPLES);
      } finally {
        setLoadingExamples(false);
      }
    };

    loadExamples();
  }, []);

  // Load user's previous sessions
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const data = await getMySessions();
        setMySessions(data.sessions || []);
      } catch (error) {
        console.error('Failed to load sessions:', error);
      } finally {
        setLoadingSessions(false);
      }
    };

    if (user) {
      loadSessions();
    }
  }, [user]);

  // Track home page view
  useEffect(() => {
    trackHomePageView();
  }, []);

  const handleLogout = async () => {
    try {
      trackLogout();
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputGoal.trim()) return;

    trackGoalSubmit(inputGoal);

    // Reset any previous session and start fresh
    reset();

    // Generate session ID
    const sessionId = crypto.randomUUID();
    setSessionId(sessionId);
    setGoal(inputGoal);
    setCurrentStep('interview');

    // Create session in Firestore immediately (non-blocking)
    createSession({ sessionId, goal: inputGoal }).catch(console.error);

    navigate('/configure');
  };

  const handleContinueSession = (session) => {
    trackSessionContinue(session.id, !!session.recommendedStack);

    // Load the session data into the store
    setSessionId(session.id);
    setGoal(session.goal);
    if (session.recommendedStack) {
      setStack(session.recommendedStack);
      setCurrentStep('stack');
      navigate('/stack');
    } else {
      setCurrentStep('interview');
      navigate('/configure');
    }
  };

  const handleExampleClick = (example) => {
    const exampleText = typeof example === 'string' ? example : example.text;
    const category = typeof example === 'string' ? 'default' : (example.category || 'default');
    trackExampleSelect(exampleText, category);
    setInputGoal(exampleText);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds ? timestamp._seconds * 1000 : timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get category count for display
  const categoryCount = [...new Set(examples.map(e => e.category || 'default'))].length;

  // Carousel state for examples
  const [examplePage, setExamplePage] = useState(0);
  const examplesPerPage = 8; // Show 8 examples at a time (2 rows of 4)
  const totalPages = Math.ceil(examples.length / examplesPerPage) || 1;

  // Get current page of examples
  const currentExamples = examples.slice(
    examplePage * examplesPerPage,
    (examplePage + 1) * examplesPerPage
  );

  // Auto-slide functionality
  const nextPage = useCallback(() => {
    setExamplePage(p => (p + 1) % totalPages);
  }, [totalPages]);

  useEffect(() => {
    if (!isAutoPlaying || examples.length === 0) return;

    const interval = setInterval(() => {
      nextPage();
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextPage, examples.length]);

  // Shuffle examples to show random selection
  const shuffleExamples = () => {
    setExamplePage(Math.floor(Math.random() * totalPages));
  };

  // Get icon for a category
  const getCategoryIcon = (category) => {
    const IconComponent = CATEGORY_ICONS[category] || CATEGORY_ICONS.default;
    return IconComponent;
  };

  // isAdmin comes from useAuth() context - checks user.role === 'admin'

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="px-6 py-4">
        <nav className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <GoldenSpiral className="w-5 h-5" color="white" />
            </div>
            <span className="font-bold text-xl">VinciKit</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="#how-it-works"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              How it works
            </a>
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-700 transition-colors text-sm font-medium"
              >
                <Settings className="w-4 h-4" />
                Admin
              </button>
            )}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{user?.email}</span>
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors text-sm"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <main className="px-6 pt-16 pb-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              The inventor's toolkit for AI-powered ideas
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight text-gray-900">
              Tell us your idea.<br />
              <span className="text-blue-600">We'll make it work.</span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              There are hundreds of AI tools out there - ChatGPT, Claude, automation apps, databases, and more.
              <strong className="text-gray-900"> How do you know which ones you actually need?</strong>
            </p>

            <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
              Just describe your project. We'll ask a few simple questions, then give you
              <strong className="text-gray-900"> exactly the right tools configured and ready to use</strong> - no research or setup required.
            </p>
          </motion.div>

          {/* Goal Input */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative">
              <input
                type="text"
                value={inputGoal}
                onChange={(e) => setInputGoal(e.target.value)}
                placeholder="I want to build..."
                className="w-full px-6 py-5 pr-36 text-lg rounded-2xl border-2 border-gray-200
                         focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100
                         shadow-sm transition-all duration-200"
              />
              <button
                type="submit"
                disabled={!inputGoal.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2
                         px-6 py-3 bg-blue-600 text-white font-medium rounded-xl
                         hover:bg-blue-700 transition-colors duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center gap-2"
              >
                Start
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.form>

          {/* Usage Banner */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 max-w-2xl mx-auto"
          >
            <UsageBanner />
          </motion.div>

          {/* Example prompts - Carousel */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                Try an example ({examples.length} ideas across {categoryCount} industries):
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                  className={`p-2 rounded-lg transition-colors ${
                    isAutoPlaying
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                  title={isAutoPlaying ? 'Pause auto-slide' : 'Resume auto-slide'}
                >
                  {isAutoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button
                  onClick={shuffleExamples}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Show random examples"
                >
                  <Shuffle className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setExamplePage(p => Math.max(0, p - 1)); setIsAutoPlaying(false); }}
                  disabled={examplePage === 0}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-xs text-gray-400 min-w-[60px] text-center">
                  {examplePage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => { setExamplePage(p => Math.min(totalPages - 1, p + 1)); setIsAutoPlaying(false); }}
                  disabled={examplePage === totalPages - 1}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <motion.div
              key={examplePage}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-2"
            >
              {currentExamples.map((example, i) => {
                const exampleText = typeof example === 'string' ? example : example.text;
                const category = typeof example === 'string' ? 'default' : (example.category || 'default');
                const IconComponent = getCategoryIcon(category);
                return (
                  <button
                    key={i}
                    onClick={() => handleExampleClick(example)}
                    className="px-3 py-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-600 text-left
                             hover:border-blue-400 hover:text-blue-600 hover:shadow-md transition-all duration-200
                             min-h-[80px] flex items-start gap-2 group"
                  >
                    <div className="w-6 h-6 rounded-md bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
                      <IconComponent className="w-3.5 h-3.5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                    </div>
                    <span className="line-clamp-3">{exampleText}</span>
                  </button>
                );
              })}
            </motion.div>

            {/* Page indicator dots with progress */}
            <div className="flex justify-center gap-1 mt-4">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setExamplePage(i); setIsAutoPlaying(false); }}
                  className={`h-2 rounded-full transition-all duration-200 ${
                    i === examplePage
                      ? 'bg-blue-600 w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* My Sessions - Previous Projects */}
        {!loadingSessions && mySessions.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="max-w-3xl mx-auto mt-16"
          >
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-gray-400" />
              <h2 className="font-semibold text-gray-900">Your Previous Projects</h2>
            </div>

            <div className="space-y-3">
              {mySessions.map((session) => (
                <motion.button
                  key={session.id}
                  onClick={() => handleContinueSession(session)}
                  className="w-full text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <h3 className="font-medium text-gray-900 truncate">
                          {session.goal}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{session.expertise || 'Not specified'}</span>
                        <span>•</span>
                        <span>{formatDate(session.createdAt)}</span>
                      </div>
                      {session.recommendedStack && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            Stack Generated
                          </span>
                          <span className="text-xs text-gray-400">
                            {session.recommendedStack.stack?.tools?.length || 0} tools • {session.recommendedStack.totalCost || 'Free'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-sm font-medium">
                        {session.recommendedStack ? 'View Stack' : 'Continue'}
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.section>
        )}

        {/* How it works */}
        <section id="how-it-works" className="max-w-5xl mx-auto mt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">How It Works</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Think of us as your personal tech consultant - but faster and always available
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Sparkles className="w-6 h-6" />,
                title: "1. Tell Us Your Idea",
                description: "Describe what you want to create in your own words. \"I want to automate my invoices\" or \"I need a chatbot for my website\" - anything goes."
              },
              {
                icon: <Cpu className="w-6 h-6" />,
                title: "2. Answer a Few Questions",
                description: "We'll ask about your budget, what tools you already use, and your comfort level with technology. This takes about 2 minutes."
              },
              {
                icon: <Wrench className="w-6 h-6" />,
                title: "3. Get Your Personalized Setup",
                description: "We'll recommend exactly which AI services and apps you need, how they work together, and what it will cost. No more Googling \"best AI tool for X\"."
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4 text-blue-600">
                  {step.icon}
                </div>
                <h3 className="font-semibold text-lg mb-2 text-gray-900">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Visual Flow Example */}
        <section className="max-w-5xl mx-auto mt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl font-bold mb-3 text-gray-900">See It In Action</h2>
            <p className="text-gray-600">Here's a real example of how VinciKit works</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100"
          >
            {/* Flow visualization */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Step 1: Your Idea */}
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-blue-600">You Say</span>
                </div>
                <p className="text-gray-800 font-medium italic">
                  "I want to automate customer support for my online store"
                </p>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <ArrowRight className="w-8 h-8 text-blue-400" />
                  <span className="text-xs text-gray-500">We ask questions</span>
                </div>
              </div>

              {/* Step 2: We Ask */}
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Cpu className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-purple-600">We Ask</span>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• What's your monthly budget?</li>
                  <li>• How many support tickets/day?</li>
                  <li>• Do you use Shopify or WooCommerce?</li>
                </ul>
              </div>
            </div>

            {/* Arrow down */}
            <div className="flex justify-center my-6">
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-green-600 rotate-90" />
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-green-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Package className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm font-medium text-green-600">You Get</span>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center text-white text-xs font-bold">C</div>
                    <span className="font-medium text-gray-900">Claude AI</span>
                  </div>
                  <p className="text-xs text-gray-500">Handles complex questions, understands context</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white text-xs font-bold">Z</div>
                    <span className="font-medium text-gray-900">Zapier</span>
                  </div>
                  <p className="text-xs text-gray-500">Connects to your store, sends responses</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center text-white text-xs font-bold">A</div>
                    <span className="font-medium text-gray-900">Airtable</span>
                  </div>
                  <p className="text-xs text-gray-500">Stores customer history & FAQs</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">Estimated cost:</span>
                  <span className="font-semibold text-gray-900">~$50/month</span>
                </div>
                <div className="flex items-center gap-2">
                  <Rocket className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600 font-medium">Ready to set up</span>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Social proof */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto mt-24 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full text-green-700 text-sm font-medium">
            <Zap className="w-4 h-4" />
            Powered by Vinci Kit • 100+ tools and APIs available
          </div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded flex items-center justify-center">
              <GoldenSpiral className="w-4 h-4" color="white" />
            </div>
            <span className="font-medium">VinciKit</span>
          </div>
          <p className="text-sm text-gray-500">
            © 2025 VinciKit. The inventor's toolkit for AI-powered ideas.
          </p>
        </div>
      </footer>
    </div>
  );
}
